import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatFabButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRipple } from '@angular/material/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { Plan } from 'src/app/interfaces';
import { Role } from 'src/app/enums';
import { PaymentPlanDialogComponent } from '../../dialogs/payment-plan-dialog/payment-plan-dialog.component';

@Component({
  selector: 'app-payment-plans',
  templateUrl: './payment-plans.component.html',
  styleUrl: './payment-plans.component.scss',
  imports: [
    MatCard,
    MatIcon,
    MatIconButton,
    MatFabButton,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    MatRipple,
    ReactiveFormsModule,
  ],
})
export class PaymentPlansComponent implements OnInit, OnDestroy {
  paymentPlans = signal<Plan[]>([]);
  filteredPaymentPlans = signal<Plan[]>([]);
  loadingPlans = signal(true);
  isSuperUser = signal(dataStore.rootUser?.rights === 'admin');
  isAdmin = signal(dataStore.orgUser?.roles?.includes(Role.Administrator) ?? false);

  displayedColumns = computed(() => {
    const cols = ['name', 'price', 'credits', 'limit', 'active'];
    if (this.isSuperUser()) cols.push('buttons');
    return cols;
  });

  searchCtrl = new FormControl('');

  private unsubscribePlans?: () => void;
  private searchSubscription?: Subscription;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    const organisationId = dataStore.selectedOrganisation.id ?? localStorage.getItem('orgId');
    if (!organisationId) {
      this.loadingPlans.set(false);
      return;
    }

    this.unsubscribePlans = onSnapshot(
      query(collection(db, `organisations/${organisationId}/plans`), orderBy('startAmount', 'asc')),
      (snapshot) => {
        const plans: Plan[] = [];
        snapshot.forEach((planDoc) => {
          plans.push({ id: planDoc.id, ...(planDoc.data() as Plan) });
        });
        this.paymentPlans.set(plans);
        this.filteredPaymentPlans.set(this.filterPlans(this.searchCtrl.value ?? '', plans));
        this.loadingPlans.set(false);
      },
      () => {
        this.loadingPlans.set(false);
      },
    );

    this.searchSubscription = this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredPaymentPlans.set(this.filterPlans(value ?? '', this.paymentPlans()));
    });
  }

  ngOnDestroy(): void {
    this.unsubscribePlans?.();
    this.searchSubscription?.unsubscribe();
  }

  clearSearch(): void {
    this.searchCtrl.setValue('');
  }

  openCreateDialog(): void {
    if (!this.isSuperUser()) {
      return;
    }
    this.dialog.open(PaymentPlanDialogComponent, {
      width: '550px',
      autoFocus: false,
      data: {},
    });
  }

  openDialog(plan: Plan): void {
    this.dialog.open(PaymentPlanDialogComponent, {
      width: '550px',
      autoFocus: false,
      data: { plan, readonly: !this.isSuperUser() },
    });
  }

  formatPrice(amount: number | undefined): string {
    return (amount ?? 0).toLocaleString('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    });
  }

  private filterPlans(searchQuery: string, plans: Plan[]): Plan[] {
    if (!searchQuery) return plans;
    const q = searchQuery.toLowerCase();
    return plans.filter((plan) => plan.name?.toLowerCase().includes(q));
  }
}
